import { memo, type Dispatch, type SetStateAction } from 'react';

import type { AttachmentDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Table } from '@/js/libs/ui';

import { useDownloadFile } from 'src/hooks/useDownloadFile';
import { AttachmentRow } from './AttachmentRow';
import type { UploadFile } from './types';
import { UploadFileRow } from './UploadFileRow';

interface AttachmentsProps {
  templateAttachments: AttachmentDto[];
  setTemplateAttachments: Dispatch<SetStateAction<AttachmentDto[]>>;
  filesToUpload?: UploadFile[];
  setFilesToUpload?: Dispatch<SetStateAction<UploadFile[]>>;
  uploading: boolean;
}

export const Attachments = memo(function Attachments({
  templateAttachments,
  setTemplateAttachments,
  filesToUpload,
  setFilesToUpload,
  uploading,
}: AttachmentsProps) {
  const { downloadFile, pendingDownloadFile } = useDownloadFile();

  const handleTemplateAttachmentDelete = useCallbackRef((attachment: AttachmentDto) => {
    setTemplateAttachments((files) => files.filter((f) => f.id !== attachment.id));
  });

  const handleTemplateAttachmentDownload = useCallbackRef(async (attachment: AttachmentDto) => {
    if (attachment.fileId) {
      downloadFile(attachment.fileId, attachment.name);
    }
  });

  const handleUploadFileDelete = useCallbackRef((file: UploadFile) => {
    setFilesToUpload?.((files) => files.filter((f) => f.id !== file.id));
  });

  if (!templateAttachments.length && !filesToUpload?.length) {
    return null;
  }

  return (
    <Table>
      <tbody>
        {templateAttachments.map((attachment) => (
          <AttachmentRow
            key={attachment.id.toString()}
            attachment={attachment}
            onDelete={handleTemplateAttachmentDelete}
            onDownload={handleTemplateAttachmentDownload}
            pendingDownloadFile={pendingDownloadFile}
          />
        ))}
        {filesToUpload?.map((file) => (
          <UploadFileRow key={file.id} uploadFile={file} onDelete={handleUploadFileDelete} uploading={uploading} />
        ))}
      </tbody>
    </Table>
  );
});
